import React, { useEffect, useState } from "react";
import { StoryblokComponent } from "@storyblok/react";
import { useLocation, useNavigate } from "react-router-dom";
import Utils from "../../core/Utils/Utils";
import { SupportLandingModel } from "./SupportLandingModel";
import SupportUtils from "../SupportUtils";
import { SupportFaqModel } from "../SupportFaq/SupportFaqModel";
import SupportFaq from "../SupportFaq/SupportFaq";

import "./SupportLanding.css";
import { Theme } from "../../core/Models/CustomSiteConfigModel";
import { themeService } from "../../core/NotificationStripNav/ThemeService";

const SupportLanding: React.FC<{ blok: SupportLandingModel }> = ({ blok }) => {
  const defaultFaqToDisplay = 6;
  const location = useLocation();
  const navigate = useNavigate();

  const [theme, setTheme] = useState<Theme | null>(themeService.getTheme());
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState("");
  const [faqToDisplay, setFaqToDisplay] = useState<number | undefined>(
    defaultFaqToDisplay
  );
  const [data, setData] = useState<SupportFaqModel[]>([]);

  useEffect(() => {
    const unsubscribe = themeService.subscribe(setTheme);
    return () => unsubscribe();
  }, []);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 500);

    return () => {
      clearTimeout(handler);
    };
  }, [searchTerm]);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const query = params.get("query") || "";
    setSearchTerm(query || "");
    setDebouncedSearchTerm(query || "");

    const fetchStories = async () => {
      try {
        const [popularStories, unpopularStories] = await Promise.all([
          SupportUtils.getCachedOrStoryblokSearch(
            query,
            faqToDisplay,
            true,
            null,
            null,
            "articles/faq/"
          ),
          SupportUtils.getCachedOrStoryblokSearch(
            query,
            faqToDisplay,
            false,
            null,
            null,
            "articles/faq/"
          ),
        ]);
        const popularStoriesArray =
          (popularStories as any).data.story ||
          (popularStories as any).data?.stories ||
          [];
        const unpopularStoriesArray =
          (unpopularStories as any).data.story ||
          (unpopularStories as any).data?.stories ||
          [];
        const combinedStories = [
          ...popularStoriesArray,
          ...unpopularStoriesArray,
        ];
        const limitedStories = combinedStories.slice(0, faqToDisplay);
        setData(limitedStories);
      } catch (error: any) {
        if (error.status === 404) {
          navigate("/404");
        } else if (error.status === 500) {
          navigate("/500");
        }
      }
    };

    fetchStories();
  }, [debouncedSearchTerm, faqToDisplay, location, navigate]);

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    Utils.setNavidation([{ param: "query", value }]);
    setSearchTerm(value);
  };

  const handleLoadMoreClick = (event: React.MouseEvent) => {
    event.preventDefault();
    setFaqToDisplay(undefined);
  };

  const handleShowLessClick = (event: React.MouseEvent) => {
    event.preventDefault();
    setFaqToDisplay(defaultFaqToDisplay);
  };

  return (
    <div
      className="support-landing"
      style={{ backgroundColor: theme?.panelColour, color: theme?.textColor }}
    >
      <div className="container">
        <div className="support-title">{blok.title}</div>
        <div className="support-subtitle">{blok.description}</div>
        <div
          className={`support-level-journeys ${
            blok.levelJourneys?.length === 3 ? "three-items" : "two-items"
          }`}
        >
          {blok.levelJourneys &&
            blok.levelJourneys.map((levelJourney) => (
              <StoryblokComponent blok={levelJourney} key={levelJourney._uid} />
            ))}
        </div>
        <div className="support-faqs">FAQs</div>
        <div className="support-filter">
          <input
            placeholder="Filter FAQs based on keyword"
            type="text"
            className="input-field"
            value={searchTerm}
            onChange={handleInputChange}
          ></input>
        </div>
        <div className="support-faqs-container">
          {data &&
            data.map((item) => (
              <SupportFaq blok={item} key={item.uuid}></SupportFaq>
            ))}
        </div>
        {faqToDisplay !== undefined && (
          <div className="support-load-more">
            <a
              href={blok._uid}
              onClick={handleLoadMoreClick}
              style={{ color: theme?.textColor }}
            >
              Load more FAQs
            </a>
          </div>
        )}
        {faqToDisplay === undefined && (
          <div className="support-load-more">
            <a
              href={blok._uid}
              onClick={handleShowLessClick}
              style={{ color: theme?.textColor }}
            >
              Show less FAQs
            </a>
          </div>
        )}
      </div>
    </div>
  );
};

export default SupportLanding;
